/**
 * DashboardAssetRow — slim List-Row-Variante für Dashboard Recent-Uploads
 * (List-View). Pendant zu `DashboardAssetTile` (Grid-View).
 *
 * Eingeführt in Slice-3 Redesign (2026-05-09).
 *
 * Layout: thumb (40×40) + Name (truncate) + Type-Pill + Filesize + Datum.
 * - 375px no-overflow (Codex C5-fix): `min-w-0` auf jedem child, Datum
 *   auf 375px hidden via `hidden sm:block`. Filesize bleibt sichtbar weil
 *   kompakt. Result: 375px Row = thumb + name (truncated) + type-pill +
 *   filesize, alles sichtbar, kein horizontal-scroll.
 *
 * Pattern analog DashboardAssetTile (Slice 2).
 */
import { Link } from 'wouter';
import type { AssetListItem } from '../lib/asset-types';
import { formatBytes } from '../lib/format-bytes';
import { formatRelative } from '../lib/format-relative';
import { I } from '../lib/icons';

function extBadge(filename: string, mime: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot >= 0) {
    const ext = filename.slice(dot + 1).toUpperCase();
    if (ext.length <= 4) return ext;
  }
  const mimeMap: Record<string, string> = {
    'image/jpeg': 'JPG',
    'image/png': 'PNG',
    'image/webp': 'WEBP',
    'image/gif': 'GIF',
    'image/avif': 'AVIF',
    'image/svg+xml': 'SVG',
    'video/mp4': 'MP4',
    'video/quicktime': 'MOV',
    'application/pdf': 'PDF',
  };
  return mimeMap[mime.toLowerCase()] ?? 'FILE';
}

const FALLBACK_BG: Record<AssetListItem['assetType'], { bg: string; fg: string }> = {
  img: { bg: 'var(--color-brand-green-200)', fg: 'var(--color-brand-green-800)' },
  video: { bg: 'var(--color-asset-video-bg)', fg: 'var(--color-brand-yellow)' },
  pdf: { bg: 'var(--color-asset-pdf-bg)', fg: 'var(--color-brand-green-700)' },
  logo: { bg: 'var(--color-brand-green-700)', fg: 'var(--color-brand-yellow)' },
};

type Props = {
  asset: AssetListItem;
};

export function DashboardAssetRow({ asset }: Props) {
  const badge = extBadge(asset.displayFilename, asset.mimeType);
  const useFallback = asset.assetType === 'logo' || !asset.thumbUrl;
  const fallback = FALLBACK_BG[asset.assetType];

  return (
    <Link
      href={`/assets/${encodeURIComponent(asset.id)}`}
      className="flex items-center gap-3 py-2 px-3 hover:bg-ink-50 border-b border-ink-100 last:border-b-0 overflow-hidden focus:outline-none focus-visible:bg-ink-50 focus-visible:ring-2 focus-visible:ring-brand-green-700 focus-visible:ring-inset"
    >
      {/* Thumb 40×40 */}
      <div className="relative w-10 h-10 flex-shrink-0 rounded overflow-hidden border border-ink-100 bg-ink-50">
        {useFallback ? (
          <div
            className="w-full h-full flex items-center justify-center font-mono text-2xs tracking-wide"
            style={{ backgroundColor: fallback.bg, color: fallback.fg }}
          >
            <span>{badge}</span>
          </div>
        ) : (
          <img
            src={asset.thumbUrl ?? undefined}
            alt={asset.displayFilename}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        )}
        {/* Play-Overlay analog AssetCard/DashboardAssetTile (Video-Erkennbarkeit
            nach Poster-Frame-Backfill). */}
        {asset.assetType === 'video' && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center">
            <span className="grid place-items-center rounded-full bg-brand-green-900/55 p-0.5 text-white">
              <I.play size={12} aria-hidden />
            </span>
          </div>
        )}
      </div>

      {/* Name */}
      <div className="flex-1 min-w-0">
        <div
          className="text-xs font-semibold text-brand-green-700 truncate leading-tight"
          title={asset.displayFilename}
        >
          {asset.displayFilename}
        </div>
      </div>

      {/* Type-Pill — kompakt */}
      <span className="flex-shrink-0 text-2xs font-mono px-1.5 py-0.5 bg-ink-100 text-brand-green-800 rounded uppercase tracking-wide">
        {badge}
      </span>

      {/* Filesize — sichtbar auch auf 375px (kompakt) */}
      <span className="flex-shrink-0 text-xs2 text-brand-green-600 font-mono tabular-nums w-16 text-right">
        {formatBytes(asset.fileSize)}
      </span>

      {/* Datum — auf 375px hidden, ab sm (640px) sichtbar */}
      <span className="hidden sm:block flex-shrink-0 text-xs2 text-brand-green-600 truncate w-24 text-right">
        {formatRelative(asset.createdAt)}
      </span>
    </Link>
  );
}
